import React from "react";
import { Space } from "antd";
import { AxRadio, AxDivider, AxButton } from "@dynata/axiom3";

class Radio extends React.Component {
  render() {
    return (
      <div className="section" id="radio">
        <h3>Radio</h3>
        <p>Used to select a single state from multiple options.</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxRadio />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props: Radio / Radio.Button</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/radio/#API"
                >
                  https://ant.design/components/radio/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>size</td>
                  <td>For button style Radio</td>
                  <td>small | medium | large</td>
                  <td></td>
                </tr>
                <tr>
                  <td>autoFocus</td>
                  <td>Whether get focus when component mounted</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>checked</td>
                  <td>Specifies whether the radio is selected</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>defaultChecked</td>
                  <td>
                    Specifies the initial state: whether or not the radio is
                    selected
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disable radio</td>
                  <td>boolean</td>
                  <td>false</td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>
                    According to value for comparison, to determine whether the
                    selected
                  </td>
                  <td>any</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/radio/#API"
                      >
                        {`<Radio />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Props: RadioGroup</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>buttonStyle</td>
                  <td>The style type of radio button</td>
                  <td>
                    <code>outline</code> | <code>solid</code>
                  </td>
                  <td>
                    <code>outline</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultValue</td>
                  <td>Default selected value</td>
                  <td>any</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disable all radio buttons</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>name</td>
                  <td>
                    The <code>name</code> property of all{" "}
                    <code>input[type="radio"]</code> children
                  </td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>options</td>
                  <td>Set children optional</td>
                  <td>
                    string[] | number[] |{" "}
                    <pre>{`Array&lt;{ label: string value: string disabled?: boolean }`}</pre>
                    &gt;
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>optionType</td>
                  <td>Set Radio optionType</td>
                  <td>
                    <code>default</code> | <code>button</code>
                  </td>
                  <td>
                    <code>default</code>
                  </td>
                  <td>4.4.0</td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>The size of radio button style</td>
                  <td>
                    <code>large</code> | <code>middle</code> |{" "}
                    <code>small</code>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>Used for setting the currently selected value</td>
                  <td>any</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onChange</td>
                  <td>
                    The callback function that is triggered when the state
                    changes
                  </td>
                  <td>function(e:Event)</td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Methods: Radio </p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>blur()</td>
                  <td>Remove focus</td>
                </tr>
                <tr>
                  <td>focus()</td>
                  <td>Get focus</td>
                </tr>
              </tbody>
            </table>
            <pre>{`
<AxRadio />
<AxRadio checked />
<AxRadio>Radio</AxRadio>
<AxRadio defaultChecked={true}>Radio</AxRadio>
<AxRadio disabled={true}>Disabled</AxRadio>
<AxRadio defaultChecked={true} disabled={true}>
  Disabled
</AxRadio>            
            `}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default
        </AxDivider>

        <Space size={16}>
          <AxRadio />
          <AxRadio checked />
          <AxRadio>Radio</AxRadio>
          <AxRadio defaultChecked={true}>Radio</AxRadio>
          <AxRadio disabled={true}>Disabled</AxRadio>
          <AxRadio defaultChecked={true} disabled={true}>
            Disabled
          </AxRadio>
        </Space>

        <AxDivider orientation="left" plain>
          Radio Group
        </AxDivider>

        <Space direction="vertical" size={16}>
          <pre>{`<AxRadio.Group defaultValue="a">
  <AxRadio value="a">Hangzhou</AxRadio>
  <AxRadio value="b">Shanghai</AxRadio>
  <AxRadio value="c">Beijing</AxRadio>
  <AxRadio value="d">Chengdu</AxRadio>
</AxRadio.Group>`}</pre>
          <AxRadio.Group defaultValue="a">
            <AxRadio value="a">Hangzhou</AxRadio>
            <AxRadio value="b">Shanghai</AxRadio>
            <AxRadio value="c">Beijing</AxRadio>
            <AxRadio value="d">Chengdu</AxRadio>
          </AxRadio.Group>
        </Space>

        <AxDivider orientation="left" plain>
          Radio Group Buttons
        </AxDivider>

        <Space direction="vertical" size={40}>
          <pre>{`<AxRadio.Group defaultValue="a" buttonStyle="solid">
  <AxRadio.Button value="a">Button</AxRadio.Button>
  <AxRadio.Button value="b">Button</AxRadio.Button>
  <AxRadio.Button value="c">Button</AxRadio.Button>
  <AxRadio.Button value="d">Button</AxRadio.Button>
  <AxRadio.Button value="e">Button</AxRadio.Button>
</AxRadio.Group>`}</pre>
          <AxRadio.Group defaultValue="a" buttonStyle="solid" size="large">
            <AxRadio.Button value="a">Button</AxRadio.Button>
            <AxRadio.Button value="b">Button</AxRadio.Button>
            <AxRadio.Button value="c" disabled>
              Button
            </AxRadio.Button>
            <AxRadio.Button value="d">Button</AxRadio.Button>
            <AxRadio.Button value="e">Button</AxRadio.Button>
            <AxRadio.Button value="f">Button</AxRadio.Button>
          </AxRadio.Group>

          <AxRadio.Group defaultValue="a" buttonStyle="solid">
            <AxRadio.Button value="a">Button</AxRadio.Button>
            <AxRadio.Button value="b">Button</AxRadio.Button>
            <AxRadio.Button value="c">Button</AxRadio.Button>
            <AxRadio.Button value="d">Button</AxRadio.Button>
            <AxRadio.Button value="e">Button</AxRadio.Button>
          </AxRadio.Group>

          <AxRadio.Group defaultValue="a" buttonStyle="solid" size="small">
            <AxRadio.Button value="a">Button</AxRadio.Button>
            <AxRadio.Button value="b">Button</AxRadio.Button>
            <AxRadio.Button value="c">Button</AxRadio.Button>
            <AxRadio.Button value="d">Button</AxRadio.Button>
            <AxRadio.Button value="e">Button</AxRadio.Button>
          </AxRadio.Group>
        </Space>
      </div>
    );
  }
}
export default Radio;
